// Operações PURAS sobre o documento do e-mail v2 (linhas → colunas →
// elementos). Recebem o documento e devolvem um novo — assim o histórico
// (desfazer) guarda fotos e nada é mutado por engano. O servidor revalida
// tudo (EmailBuilder::LayoutCatalog.normalize); aqui a regra é não deixar a
// tela num estado que ele teria de corrigir (rodapé, larguras).
import { newId } from './ids';

const clone = value => JSON.parse(JSON.stringify(value));

export const isFooterRow = row => row?.role === 'footer';

// Limites do servidor (LayoutCatalog::LIMITS): lá o excesso é cortado em
// silêncio, então aqui a operação que passaria do limite é recusada.
export const DEFAULT_LIMITS = { rows: 60, columns: 4, elements: 20 };

export class LimitError extends Error {
  constructor(kind) {
    super(`limit:${kind}`);
    this.name = 'LimitError';
    this.kind = kind;
  }
}

const limitOf = (limits, key) => limits?.[key] ?? DEFAULT_LIMITS[key];

const contentRowCount = doc => doc.rows.filter(r => !isFooterRow(r)).length;

const assertRoom = (count, adding, limits, key) => {
  if (count + adding > limitOf(limits, key)) throw new LimitError(key);
};

const footerIndex = doc => {
  const idx = doc.rows.findIndex(isFooterRow);
  return idx < 0 ? doc.rows.length : idx;
};

export const normalizeWidths = widths => {
  const ws = widths.map(w =>
    Math.min(100, Math.max(10, Math.round(Number(w) || 0)))
  );
  const total = ws.reduce((a, b) => a + b, 0);
  if (total === 100) return ws;
  const scaled = ws.map(w => Math.max(10, Math.floor((w / total) * 100)));
  scaled[scaled.length - 1] += 100 - scaled.reduce((a, b) => a + b, 0);
  if (scaled[scaled.length - 1] >= 10) return scaled;
  const even = Math.floor(100 / ws.length);
  return ws.map((_, i) =>
    i === ws.length - 1 ? 100 - even * (ws.length - 1) : even
  );
};

// Ao tirar uma coluna, as que ficam dividem o espaço por igual.
export const equalWidths = count => {
  const even = Math.floor(100 / count);
  return Array.from({ length: count }, (_, i) =>
    i === count - 1 ? 100 - even * (count - 1) : even
  );
};

export const buildElement = (catalog, type) => ({
  id: newId('el'),
  type,
  ...clone(catalog?.layout?.defaults?.elements?.[type] || {}),
});

const buildColumn = (catalog, width, elements = []) => ({
  id: newId('col'),
  width,
  valign: 'top',
  style: clone(catalog?.layout?.defaults?.column || {}),
  elements,
});

export const buildRow = (catalog, widths, elements = []) => ({
  id: newId('row'),
  style: clone(catalog?.layout?.defaults?.row || {}),
  columns: widths.map((w, i) => buildColumn(catalog, w, elements[i] || [])),
});

export const cloneWithNewIds = node => {
  const copy = clone(node);
  if (copy.columns) {
    copy.id = newId('row');
    delete copy.role;
    copy.columns = copy.columns.map(col => ({
      ...col,
      id: newId('col'),
      elements: col.elements.map(el => ({ ...el, id: newId('el') })),
    }));
  } else if (copy.elements) {
    copy.id = newId('col');
    copy.elements = copy.elements.map(el => ({ ...el, id: newId('el') }));
  } else {
    copy.id = newId('el');
  }
  return copy;
};

export const buildSectionRows = section =>
  (section?.rows || []).map(cloneWithNewIds);

export const findNode = (doc, id) => {
  if (!id || !doc) return null;
  for (let rowIndex = 0; rowIndex < doc.rows.length; rowIndex += 1) {
    const row = doc.rows[rowIndex];
    if (row.id === id) return { kind: 'row', row, rowIndex };
    for (let colIndex = 0; colIndex < row.columns.length; colIndex += 1) {
      const column = row.columns[colIndex];
      if (column.id === id) {
        return { kind: 'column', row, rowIndex, column, colIndex };
      }
      const elIndex = column.elements.findIndex(e => e.id === id);
      if (elIndex >= 0) {
        return {
          kind: 'element',
          row,
          rowIndex,
          column,
          colIndex,
          element: column.elements[elIndex],
          elIndex,
        };
      }
    }
  }
  return null;
};

export const insertRows = (doc, rows, index, limits) => {
  assertRoom(contentRowCount(doc), rows.length, limits, 'rows');
  const out = clone(doc);
  const end = footerIndex(out);
  const at = Math.min(index ?? end, end);
  out.rows.splice(Math.max(0, at), 0, ...clone(rows));
  return out;
};

export const moveRow = (doc, rowId, toIndex) => {
  const found = findNode(doc, rowId);
  if (!found || found.kind !== 'row' || isFooterRow(found.row)) return doc;
  const out = clone(doc);
  const [row] = out.rows.splice(found.rowIndex, 1);
  const end = footerIndex(out);
  out.rows.splice(Math.max(0, Math.min(toIndex, end)), 0, row);
  return out;
};

export const insertElement = (doc, columnId, element, index, limits) => {
  const out = clone(doc);
  const found = findNode(out, columnId);
  if (!found || found.kind !== 'column') return doc;
  const list = found.column.elements;
  assertRoom(list.length, 1, limits, 'elements');
  list.splice(
    Math.max(0, Math.min(index ?? list.length, list.length)),
    0,
    clone(element)
  );
  return out;
};

export const moveElement = (doc, elementId, toColumnId, toIndex, limits) => {
  const out = clone(doc);
  const from = findNode(out, elementId);
  const to = findNode(out, toColumnId);
  if (!from || from.kind !== 'element' || !to || to.kind !== 'column') {
    return doc;
  }
  if (from.column.id !== to.column.id) {
    assertRoom(to.column.elements.length, 1, limits, 'elements');
  }
  const [el] = from.column.elements.splice(from.elIndex, 1);
  const list = to.column.elements;
  list.splice(Math.max(0, Math.min(toIndex, list.length)), 0, el);
  return out;
};

export const removeNode = (doc, id) => {
  const found = findNode(doc, id);
  if (!found || (found.kind === 'row' && isFooterRow(found.row))) return doc;
  const out = clone(doc);
  const row = out.rows[found.rowIndex];
  if (found.kind === 'row') {
    out.rows.splice(found.rowIndex, 1);
  } else if (found.kind === 'column') {
    if (isFooterRow(row) && row.columns.length === 1) return doc;
    row.columns.splice(found.colIndex, 1);
    if (!row.columns.length) {
      out.rows.splice(found.rowIndex, 1);
    } else {
      equalWidths(row.columns.length).forEach((w, i) => {
        row.columns[i].width = w;
      });
    }
  } else {
    row.columns[found.colIndex].elements.splice(found.elIndex, 1);
  }
  return out;
};

export const duplicateNode = (doc, id, limits) => {
  const found = findNode(doc, id);
  if (
    !found ||
    found.kind === 'column' ||
    (found.kind === 'row' && isFooterRow(found.row))
  ) {
    return { doc, id };
  }
  if (found.kind === 'row') {
    const copy = cloneWithNewIds(found.row);
    return {
      doc: insertRows(doc, [copy], found.rowIndex + 1, limits),
      id: copy.id,
    };
  }
  const copy = cloneWithNewIds(found.element);
  return {
    doc: insertElement(doc, found.column.id, copy, found.elIndex + 1, limits),
    id: copy.id,
  };
};

export const updateNode = (doc, id, patch) => {
  const out = clone(doc);
  const found = findNode(out, id);
  if (!found) return doc;
  const target = found[found.kind];
  const { style, ...rest } = patch;
  Object.assign(target, clone(rest));
  if (style) target.style = { ...(target.style || {}), ...clone(style) };
  return out;
};

export const setColumnWidths = (doc, rowId, widths) => {
  const out = clone(doc);
  const found = findNode(out, rowId);
  if (
    !found ||
    found.kind !== 'row' ||
    widths.length !== found.row.columns.length
  ) {
    return doc;
  }
  normalizeWidths(widths).forEach((w, i) => {
    found.row.columns[i].width = w;
  });
  return out;
};

// Coluna que some passa os elementos para a última que fica; o que passar do
// limite por coluna vai para linha(s) nova(s) logo abaixo, de uma coluna.
const spillRows = (row, elements, max) => {
  const lastCol = row.columns[row.columns.length - 1];
  const rows = [];
  for (let i = 0; i < elements.length; i += max) {
    rows.push({
      id: newId('row'),
      style: clone(row.style || {}),
      columns: [
        {
          id: newId('col'),
          width: 100,
          valign: 'top',
          style: clone(lastCol.style || {}),
          elements: elements.slice(i, i + max),
        },
      ],
    });
  }
  return rows;
};

export const setStructure = (doc, rowId, widths, limits) => {
  if (widths.length > limitOf(limits, 'columns')) {
    throw new LimitError('columns');
  }
  const out = clone(doc);
  const found = findNode(out, rowId);
  if (!found || found.kind !== 'row') return doc;
  const cols = found.row.columns;
  if (widths.length < cols.length) {
    const removed = cols.splice(widths.length);
    const last = cols[cols.length - 1];
    const merged = [...last.elements, ...removed.flatMap(c => c.elements)];
    const max = limitOf(limits, 'elements');
    last.elements = merged.slice(0, max);
    const extra = spillRows(found.row, merged.slice(max), max);
    if (extra.length) {
      assertRoom(contentRowCount(out), extra.length, limits, 'rows');
      const at = Math.min(found.rowIndex + 1, footerIndex(out));
      out.rows.splice(at, 0, ...extra);
    }
  }
  while (cols.length < widths.length) {
    cols.push({
      id: newId('col'),
      width: 0,
      valign: 'top',
      style: clone(cols[0]?.style || {}),
      elements: [],
    });
  }
  normalizeWidths(widths).forEach((w, i) => {
    cols[i].width = w;
  });
  return out;
};

export const pasteNode = (doc, clip, selectedId, limits) => {
  if (!clip?.node) return { doc, id: null };
  const sel = findNode(doc, selectedId);
  const copy = cloneWithNewIds(clip.node);
  if (clip.kind === 'row') {
    const at = sel ? sel.rowIndex + 1 : undefined;
    return { doc: insertRows(doc, [copy], at, limits), id: copy.id };
  }
  if (sel?.kind === 'element') {
    return {
      doc: insertElement(doc, sel.column.id, copy, sel.elIndex + 1, limits),
      id: copy.id,
    };
  }
  if (sel?.kind === 'column') {
    return {
      doc: insertElement(doc, sel.column.id, copy, undefined, limits),
      id: copy.id,
    };
  }
  if (sel?.kind === 'row') {
    return {
      doc: insertElement(doc, sel.row.columns[0].id, copy, undefined, limits),
      id: copy.id,
    };
  }
  return { doc, id: null };
};

// Bloco salvo ("Meus blocos"), sempre com ids novos. Linha entra no índice
// pedido ou logo depois da seleção; elemento entra na coluna pedida, perto da
// seleção ou, sem seleção (ou com o rodapé selecionado), numa linha nova.
export const insertSavedNode = (doc, catalog, saved, targetArg, limits) => {
  const target = targetArg || {};
  if (!saved?.node || !['row', 'element'].includes(saved.kind)) {
    return { doc, id: null };
  }
  const copy = cloneWithNewIds(saved.node);
  const sel = findNode(doc, target.selectedId);
  const afterSel = sel ? sel.rowIndex + 1 : undefined;
  if (saved.kind === 'row') {
    const at = target.rowIndex ?? afterSel;
    return { doc: insertRows(doc, [copy], at, limits), id: copy.id };
  }
  if (target.columnId) {
    return {
      doc: insertElement(doc, target.columnId, copy, target.index, limits),
      id: copy.id,
    };
  }
  const inBody = sel && !isFooterRow(sel.row);
  if (inBody && (sel.kind === 'element' || sel.kind === 'column')) {
    const index = sel.kind === 'element' ? sel.elIndex + 1 : undefined;
    return {
      doc: insertElement(doc, sel.column.id, copy, index, limits),
      id: copy.id,
    };
  }
  if (inBody && sel.kind === 'row') {
    return {
      doc: insertElement(doc, sel.row.columns[0].id, copy, undefined, limits),
      id: copy.id,
    };
  }
  const row = buildRow(catalog, [100], [[copy]]);
  return { doc: insertRows(doc, [row], afterSel, limits), id: copy.id };
};
